/**
 * NewClient — `/clients/new`. The "+ Add client" page.
 *
 * Backs the POST /api/clients endpoint. Required fields mirror the
 * server-side createSchema (firstName + surname); everything else is
 * optional and matches the same Zod constraints (email shape, ISO
 * date, segment + riskProfile enums). On success we navigate straight
 * to the new client's detail page so the adviser can keep working.
 *
 * Permission-gated on `client.edit`. The dedupe scan + Next-Task.ai
 * event both fire server-side in `setImmediate(...)` blocks, so the
 * UX here doesn't need to wait on either — a fuzzy duplicate just
 * lands in `/admin/match-candidates` for review later.
 */
import { useState } from 'react';
import { useLocation, Link } from 'wouter';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import type { ClientRecord } from '../../../shared/api';
import type { Segment, RiskProfile } from '../../../shared/types';
import { SEGMENTS } from '../../../shared/types';

type SegmentChoice = Segment | '';
type RiskProfileChoice = RiskProfile | '';

interface FormBody {
  firstName: string;
  surname: string;
  email1: string;
  phoneMobile: string;
  dateOfBirth: string;
  segment: SegmentChoice;
  riskProfile: RiskProfileChoice;
  suburb: string;
  state: string;
  postcode: string;
}

const EMPTY: FormBody = {
  firstName: '',
  surname: '',
  email1: '',
  phoneMobile: '',
  dateOfBirth: '',
  segment: '',
  riskProfile: '',
  suburb: '',
  state: '',
  postcode: '',
};

const RISK_PROFILES: ReadonlyArray<RiskProfile> = [
  'Conservative',
  'Moderately Conservative',
  'Balanced',
  'Growth',
  'High Growth',
];

/** Strip empties so we don't send `""` to a zod-validated `.email().optional()` etc. */
function buildPayload(b: FormBody): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(b)) {
    if (typeof v === 'string' && v.trim().length > 0) out[k] = v.trim();
  }
  return out;
}

export function NewClient() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [body, setBody] = useState<FormBody>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!can(user?.role, 'client.edit')) {
    return (
      <div className="max-w-2xl">
        <p className="qa-eyebrow mb-3">Add client</p>
        <h1 className="qa-h1 mb-4">No <span className="qa-italic-accent">access.</span></h1>
        <p className="qa-deck text-ink-soft mb-6">
          Your role doesn't have permission to create clients. Ask an admin if you need it.
        </p>
        <Link to="/" className="btn-ghost inline-block">← Back to dashboard</Link>
      </div>
    );
  }

  function set<K extends keyof FormBody>(key: K, value: FormBody[K]) {
    setBody(b => ({ ...b, [key]: value }));
    if (error) setError(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await api<{ data: ClientRecord }>('/api/clients', {
        method: 'POST',
        body: JSON.stringify(buildPayload(body)),
      });
      setLocation(`/clients/${resp.data.id}`);
    } catch (e) {
      if (e instanceof ApiError) {
        // Server returns `details` as a list of zod issues on validation failure.
        // We surface the first one — it's almost always the one the user can act on.
        const details = e.details as Array<{ path?: Array<string | number>; message?: string }> | undefined;
        const first = details?.[0];
        if (first?.message) {
          const field = first.path?.join('.') ?? 'form';
          setError(`${field}: ${first.message}`);
        } else {
          setError(e.message || 'Could not create client.');
        }
      } else {
        setError('Could not create client. Please try again.');
      }
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-8 qa-rise qa-stagger-1">
        <p className="qa-eyebrow mb-2">Add client</p>
        <h1 className="qa-h1">
          A new face on the <span className="qa-italic-accent">book.</span>
        </h1>
        <p className="qa-deck mt-2 text-ink-soft">
          Capture the essentials now. The full fact-find can come later
          through Fact-Find.AI — we'll merge the submission against this record.
        </p>
      </div>

      <form onSubmit={onSubmit} className="qa-card p-8 space-y-6 qa-rise qa-stagger-2" noValidate>
        {/* Identity ----------------------------------------------------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="qa-label" htmlFor="nc-first">First name</label>
            <input
              id="nc-first"
              type="text"
              required
              maxLength={120}
              className="qa-input"
              autoComplete="off"
              value={body.firstName}
              onChange={e => set('firstName', e.target.value)}
            />
          </div>
          <div>
            <label className="qa-label" htmlFor="nc-surname">Surname</label>
            <input
              id="nc-surname"
              type="text"
              required
              maxLength={120}
              className="qa-input"
              autoComplete="off"
              value={body.surname}
              onChange={e => set('surname', e.target.value)}
            />
          </div>
        </div>

        {/* Contact ------------------------------------------------------ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="qa-label" htmlFor="nc-email">Email</label>
            <input
              id="nc-email"
              type="email"
              className="qa-input"
              autoComplete="off"
              value={body.email1}
              onChange={e => set('email1', e.target.value)}
            />
          </div>
          <div>
            <label className="qa-label" htmlFor="nc-mobile">Mobile</label>
            <input
              id="nc-mobile"
              type="tel"
              className="qa-input"
              autoComplete="off"
              value={body.phoneMobile}
              onChange={e => set('phoneMobile', e.target.value)}
            />
          </div>
        </div>

        {/* Date of birth + segmentation -------------------------------- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="qa-label" htmlFor="nc-dob">Date of birth</label>
            <input
              id="nc-dob"
              type="date"
              className="qa-input"
              value={body.dateOfBirth}
              onChange={e => set('dateOfBirth', e.target.value)}
            />
          </div>
          <div>
            <label className="qa-label" htmlFor="nc-segment">Segment</label>
            <select
              id="nc-segment"
              className="qa-select"
              value={body.segment}
              onChange={e => set('segment', e.target.value as SegmentChoice)}
            >
              <option value="">—</option>
              {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="qa-label" htmlFor="nc-rp">Risk profile</label>
            <select
              id="nc-rp"
              className="qa-select"
              value={body.riskProfile}
              onChange={e => set('riskProfile', e.target.value as RiskProfileChoice)}
            >
              <option value="">—</option>
              {RISK_PROFILES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {/* Address (lightweight — full fact-find handles the rest) ----- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="qa-label" htmlFor="nc-suburb">Suburb</label>
            <input
              id="nc-suburb"
              type="text"
              className="qa-input"
              autoComplete="off"
              value={body.suburb}
              onChange={e => set('suburb', e.target.value)}
            />
          </div>
          <div>
            <label className="qa-label" htmlFor="nc-state">State</label>
            <input
              id="nc-state"
              type="text"
              maxLength={3}
              className="qa-input"
              autoComplete="off"
              placeholder="QLD, NSW…"
              value={body.state}
              onChange={e => set('state', e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <label className="qa-label" htmlFor="nc-postcode">Postcode</label>
            <input
              id="nc-postcode"
              type="text"
              inputMode="numeric"
              maxLength={4}
              className="qa-input"
              autoComplete="off"
              value={body.postcode}
              onChange={e => set('postcode', e.target.value.replace(/\D/g, ''))}
            />
          </div>
        </div>

        {error && (
          <div role="alert" aria-live="polite" className="qa-badge qa-badge--out">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || !body.firstName.trim() || !body.surname.trim()}
          >
            {busy ? 'Creating…' : 'Create client'}
          </button>
          <Link to="/" className="btn-ghost">Cancel</Link>
          <span className="ml-auto text-xs text-ink-mute">
            Required: first name, surname
          </span>
        </div>
      </form>
    </div>
  );
}
